import { useState, useEffect } from 'react';
import { AlertOctagon, Pill, ArrowRight } from 'lucide-react';
import { clsx } from 'clsx';
import api from '../config/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const PAGE_SIZE = 50;

export default function LowStock() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadLowStock();
  }, [page]);

  const loadLowStock = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/alerts/low-stock?page=${page}&limit=${PAGE_SIZE}`);
      const list = res?.items || [];
      setItems(list.map((item) => ({
        ...item,
        stock: Number(item.stock),
        severity: Number(item.stock) <= Number(item.reorder_level || 0) * 0.5 ? 'critical' : 'warning',
      })));
      setTotal(res?.pagination?.total ?? list.length);
      setPages(res?.pagination?.pages ?? 1);
    } catch (e) {
      toast.error(e.message || 'Failed to load low stock data');
    } finally {
      setLoading(false);
    }
  };

  const severityConfig = {
    critical: { bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', bar: 'bg-red-500' },
    warning: { bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500' },
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-amber-500" />
            <h2 className="text-base font-semibold text-gray-900">Low Stock Warning</h2>
          </div>
          <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold text-amber-700">
            {total.toLocaleString()} items below reorder level
          </span>
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400">Loading low-stock alerts...</div>
        ) : items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Medicine</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Current Stock</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Reorder Level</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Stock Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => {
                  const config = severityConfig[item.severity] || severityConfig.warning;
                  const pct = Math.min((item.stock / (item.reorder_level || 1)) * 100, 100);
                  return (
                    <tr key={item.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                            <Pill className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                            <p className="text-xs text-gray-400">{item.generic_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={clsx('inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold', config.badge)}>
                          {item.stock} {item.unit}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.reorder_level} {item.unit}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-32">
                          <div className="h-2 rounded-full bg-gray-200">
                            <div className={clsx('h-2 rounded-full transition-all', config.bar)} style={{ width: `${pct}%` }} />
                          </div>
                          <p className="mt-1 text-[10px] font-semibold text-gray-400">{Math.round(pct)}% of reorder level</p>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Pill className="mb-3 h-12 w-12 text-emerald-300" />
            <p className="text-sm font-medium text-emerald-600">All medicines well-stocked!</p>
            <p className="text-xs text-gray-400 mt-1">No items are below their reorder level</p>
          </div>
        )}
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
            <p className="text-sm text-gray-500">Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}</p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1 || loading} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">Previous</button>
              <span className="text-sm font-medium text-gray-600">Page {page} of {pages}</span>
              <button type="button" onClick={() => setPage((current) => Math.min(pages, current + 1))} disabled={page >= pages || loading} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <button onClick={() => navigate('/medicines')} className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-700">
          View All Medicines <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
